import { CheckCircle2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../components/LoadingSpinner';
import PaymentPinSplitLayout from '../components/Payment/PaymentPinSplitLayout';
import { DEFAULT_CREDIT_CHECKOUT_PLAN, readCheckoutActivePlanId, clearCheckoutActivePlan } from '../components/CreditSystem/creditCheckoutUtils';
import { formatPlanInvoiceDescription, transactionAmountToRupees } from '../config/creditPacks';
import { supabase } from '../lib/supabase';
import { getInvoiceByOrderId, downloadInvoicePDF, createInvoice } from '../services/invoiceService';
import { pushUserNotification } from '../services/objectivesTasksService';
import { recordSubscriptionPaymentFailed, verifyPackSubscription } from '../services/packSubscriptionService';
import { creditCheckoutUrl, goToCheckoutAfterPaymentFailure, goToPaymentFailed } from '../utils/creditCheckoutNavigation';
import { SEO } from '../shared/components/SEO';
import { getAppOrigin } from '../shared/seo/siteOrigin';
import { clearPaymentFlowFlags, clearSessionRecoveryToken, isPaymentFlowInProgress, readSessionRecoveryToken, stashPaymentReturnTarget } from '../utils/paymentReturnBootstrap';
import { trackPurchase } from '../utils/metaPixel';
import { trackGa4Purchase, trackGa4Subscribe, trackGoogleAdsConversion } from '../utils/gtag';

type InvoiceLang = 'en' | 'pt' | 'es' | 'ru';
function resolveInvoiceLang(i18nLang: string): InvoiceLang {
  const l = i18nLang?.split('-')[0]?.toLowerCase();
  if (l === 'pt') return 'pt';
  if (l === 'es') return 'es';
  if (l === 'ru') return 'ru';
  return 'en';
}

type VerifyingStep = 'connecting' | 'checking' | 'confirming' | 'retrying';
type PageStatus = 'verifying' | 'success' | 'pending';

const VERIFYING_STEP_KEYS: Record<VerifyingStep, string> = {
  connecting: 'verifyingStepConnecting',
  checking: 'verifyingStepChecking',
  confirming: 'verifyingStepConfirming',
  retrying: 'verifyingStepRetrying',
};

const MAX_VERIFICATION_TIME_SEC = 45;

const SUBSCRIPTION_SUCCESS_STATUSES = new Set(['active', 'bank_approval_pending']);
const SUBSCRIPTION_TERMINAL_FAIL_STATUSES = new Set([
  'cancelled', 'customer_cancelled', 'customer_paused', 'failed', 'expired',
  'link_expired', 'inactive', 'halted', 'card_expired', 'completed',
]);
const SUBSCRIPTION_PENDING_STATUSES = new Set([
  'initialized',
  'initialised',
  'pending',
  'on_hold',
  'created',
]);
const SUBSCRIPTION_MAX_VERIFY_SEC = 60;
const SUBSCRIPTION_POLL_MS = 3000;
const SUBSCRIPTION_MAX_ATTEMPTS = 10;

function isCashfreeCheckoutFailureFromUrl(params: URLSearchParams): boolean {
  const hints = [
    params.get('cf_checkoutStatus'),
    params.get('cf_authStatus'),
    params.get('authorization_status'),
    params.get('payment_status'),
    params.get('subscription_status'),
    params.get('cf_status'),
    params.get('txStatus'),
    params.get('tx_status'),
    params.get('order_status'),
    params.get('error_code'),
    params.get('error_message'),
  ].filter(Boolean).join(' ');
  return /fail|cancel|declin|drop|void|user_dropped|abandon|terminated|expired/i.test(hints);
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface PaymentSuccessPageProps {
  onNavigate?: (view: string) => void;
}

const PaymentSuccessPage: React.FC<PaymentSuccessPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const invoiceLang = resolveInvoiceLang(i18n.language);
  const [status, setStatus] = useState<PageStatus>('verifying');
  const [message, setMessage] = useState('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [verifyingStep, setVerifyingStep] = useState<VerifyingStep>('connecting');
  const [attempt, setAttempt] = useState(1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [customerName, setCustomerName] = useState<string>('');
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [isRecurringFlow, setIsRecurringFlow] = useState(false);
  const [retryPlanId, setRetryPlanId] = useState(DEFAULT_CREDIT_CHECKOUT_PLAN);
  const isRecurringFlowRef = useRef(false);
  const verificationStartTimeRef = useRef<number | null>(null);
  const verificationEndTimeRef = useRef<number | null>(null);
  const redirectingToFailedRef = useRef(false);
  const sessionRefreshAttemptedRef = useRef(false);
  const userEmailRef = useRef<string | null>(null);
  const paymentEmailSentRef = useRef(false);

  // Mark this page as actively rendered so AppRouter guards don't bounce to '/'
  // after clearPaymentFlowFlags() removes localStorage flags mid-verification.
  useEffect(() => {
    try { sessionStorage.setItem('ggj_payment_page_active', '/payment-success'); } catch { /* */ }
    return () => {
      try { sessionStorage.removeItem('ggj_payment_page_active'); } catch { /* */ }
    };
  }, []);

  const formatTxnAmountInr = (amount: number | string | null | undefined): string => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return '';
    // credit_transactions.amount is stored in paise (e.g. 41182 = ₹411.82)
    const rupees = n >= 1000 ? n / 100 : n;
    return rupees.toFixed(2);
  };

  const firePaymentEmail = (emailType: 'payment_success' | 'payment_failed', extras: Record<string, unknown> = {}) => {
    if (!userEmailRef.current) return;
    if (emailType === 'payment_success' && paymentEmailSentRef.current) return;
    if (emailType === 'payment_success') paymentEmailSentRef.current = true;
    supabase.functions.invoke('send-funnel-email', {
      body: { type: emailType, email: userEmailRef.current, ...extras },
    }).then(({ data, error }) => {
      if (error) console.warn(`[PaymentEmail] ${emailType} failed:`, error);
      else console.log(`[PaymentEmail] ${emailType} sent ✅ resend_id:`, data?.resend_id);
    }).catch(() => {});
  };

  /** One email: payment confirmation + invoice PDF (PDF generated server-side in send-funnel-email). */
  const notifyPaymentSuccess = async (
    orderIdForNotify: string,
    creditsAdded: number,
    rawAmount: number | string | null | undefined,
  ) => {
    if (!userEmailRef.current || paymentEmailSentRef.current) return;
    paymentEmailSentRef.current = true;

    const { data, error } = await supabase.functions.invoke('send-funnel-email', {
      body: {
        type: 'payment_success',
        email: userEmailRef.current,
        credits: creditsAdded,
        amount: formatTxnAmountInr(rawAmount),
        order_id: orderIdForNotify,
      },
    });

    if (error) {
      console.warn('[PaymentEmail] payment_success failed:', error);
      paymentEmailSentRef.current = false;
      return;
    }
    console.log(
      '[PaymentEmail] payment_success sent:',
      data?.resend_id,
      'pdf attached:',
      data?.has_pdf_attachment,
    );
  };

  // Download invoice as PDF - looks up stored invoice or creates one on-the-fly
  const handleDownloadReceipt = async () => {
    if (!orderId || downloadingPDF) return;
    setDownloadingPDF(true);
    try {
      // First try to find the invoice in the DB
      let invoice = await getInvoiceByOrderId(orderId);

      // If not found, build from credit_transactions (plan + rupee amounts from checkout metadata)
      if (!invoice) {
        const { data: txn } = await supabase
          .from('credit_transactions')
          .select('amount, credits_amount, metadata, description')
          .eq('order_id', orderId)
          .maybeSingle();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const name = customerName || user.user_metadata?.full_name || user.email?.split('@')[0] || 'Customer';
          const meta = (txn?.metadata ?? {}) as Record<string, unknown>;
          const preTax = Number(meta.pre_tax_amount);
          const gst = Number(meta.gst_amount);
          const totalRupees = preTax > 0
            ? parseFloat((preTax + (Number.isFinite(gst) ? gst : 0)).toFixed(2))
            : transactionAmountToRupees(txn?.amount ?? JSON.parse(localStorage.getItem('paymentSuccess') || '{}').amount);
          const planId = typeof meta.plan_id === 'string' ? meta.plan_id : undefined;
          const lineDescription = formatPlanInvoiceDescription(planId, txn?.credits_amount);
          invoice = await createInvoice({
            order_id: orderId,
            customer_name: name,
            customer_email: user.email || '',
            items: [{ description: lineDescription, quantity: 1, unit_price: totalRupees, total: totalRupees }],
            subtotal: totalRupees,
            total_amount: totalRupees,
            payment_method: 'cashfree',
            payment_status: 'paid',
            payment_date: new Date().toISOString(),
            notes: planId ? `Thank you for subscribing to GoGrabJob.` : undefined,
          });
        }
      }

      if (invoice) {
        await downloadInvoicePDF(invoice, invoiceLang);
      }
    } catch (_) {
      console.warn('PDF download failed');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const navigateFromPaymentResult = (view: 'dashboard' | 'checkout', pathForHref: string) => {
    if (view === 'checkout') {
      goToCheckoutAfterPaymentFailure(retryPlanId || readCheckoutActivePlanId());
      return;
    }
    try {
      clearPaymentFlowFlags();
      clearCheckoutActivePlan();
    } catch (_) {}
    const absolute = pathForHref.startsWith('http')
      ? pathForHref
      : `${getAppOrigin()}${pathForHref}`;
    window.location.replace(absolute);
  };

  const rememberRetryPlan = (planId?: string | null) => {
    const resolved = planId?.trim() || readCheckoutActivePlanId();
    setRetryPlanId(resolved);
  };

  const markPaymentReturnActive = (orderIdForStash?: string | null, subscriptionId?: string | null) => {
    try {
      localStorage.setItem('payment_in_progress', 'true');
      sessionStorage.setItem('tab_session_active', Date.now().toString());
      if (subscriptionId) {
        stashPaymentReturnTarget({ type: 'recurring', subscriptionId });
      } else if (orderIdForStash) {
        stashPaymentReturnTarget({ orderId: orderIdForStash });
      }
    } catch {
      /* best-effort */
    }
  };

  /** Verification failed — notify in background, then redirect to the dedicated failure page. */
  const showPaymentFailure = (failureMessage: string, opts?: { notify?: boolean; planId?: string | null }) => {
    if (redirectingToFailedRef.current) return;
    redirectingToFailedRef.current = true;

    verificationEndTimeRef.current = Date.now();
    const resolvedPlan = opts?.planId?.trim() || readCheckoutActivePlanId() || retryPlanId || DEFAULT_CREDIT_CHECKOUT_PLAN;
    rememberRetryPlan(resolvedPlan);

    // Tear down guards before any async work so bootstrap / AppRouter cannot
    // bounce us back to /payment-success while we redirect.
    clearPaymentFlowFlags();

    if (opts?.notify !== false) {
      void (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user?.id) {
            await pushUserNotification(session.user.id, {
              type: 'payment_verification_failed',
              entity_type: 'payment',
              message: failureMessage,
            });
          }
        } catch {
          /* best-effort */
        }
      })();
    }

    const failParams = new URLSearchParams(window.location.search);
    const urlOrderId = failParams.get('order_id');
    const urlSubscriptionId = failParams.get('subscription_id');
    const isRecurringFailure =
      isRecurringFlowRef.current || failParams.get('type') === 'recurring' || !!urlSubscriptionId;
    goToPaymentFailed({
      reason: failureMessage,
      planId: resolvedPlan,
      orderId: isRecurringFailure ? null : (orderId || urlOrderId),
      subscriptionId: urlSubscriptionId || (isRecurringFailure ? orderId : null),
      recurring: isRecurringFailure,
      elapsedSeconds: Math.max(0, elapsedSeconds),
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionIdFromUrl = urlParams.get('subscription_id');
    const isRecurring = urlParams.get('type') === 'recurring';
    const orderIdFromUrl = urlParams.get('order_id');
    const urlIndicatesFailure = isCashfreeCheckoutFailureFromUrl(urlParams);

    if (subscriptionIdFromUrl && isRecurring) {
      setIsRecurringFlow(true);
      isRecurringFlowRef.current = true;
      verificationStartTimeRef.current = Date.now();
      setOrderId(subscriptionIdFromUrl);

      const failSubscription = (failureMessage: string) => {
        rememberRetryPlan(readCheckoutActivePlanId());
        showPaymentFailure(failureMessage, { notify: false });
        if (subscriptionIdFromUrl) {
          void recordSubscriptionPaymentFailed(subscriptionIdFromUrl, failureMessage);
        }
      };

      const authFailedMessage = t('views.paymentSuccess.subscriptionAuthFailed', {
        defaultValue: 'Subscription authorization was not completed. You can try again from checkout.',
      });

      if (urlIndicatesFailure) {
        failSubscription(authFailedMessage);
        return;
      }

      if (!urlIndicatesFailure) {
        markPaymentReturnActive(null, subscriptionIdFromUrl);
      }

      let cancelled = false;

      const verifySubscription = async (attemptNum = 1) => {
        if (cancelled || redirectingToFailedRef.current) return;
        setAttempt(attemptNum);
        setVerifyingStep(attemptNum > 1 ? 'retrying' : 'checking');

        const result = await verifyPackSubscription(subscriptionIdFromUrl);
        const subStatus = String(result.status || '').toLowerCase();

        if (SUBSCRIPTION_SUCCESS_STATUSES.has(subStatus)) {
          if (redirectingToFailedRef.current) return;
          verificationEndTimeRef.current = Date.now();
          setStatus('success');
          setMessage(t('views.paymentSuccess.subscriptionActive', { defaultValue: 'Your subscription is active. Credits have been added.' }));
          try { window.dispatchEvent(new Event('planStatusRefresh')); } catch (_) {}

          let { data: { session } } = await supabase.auth.getSession();
          // Recovery: if localStorage was wiped by mobile UPI redirect, restore from cookie
          if (!session) {
            const rt = readSessionRecoveryToken();
            if (rt) {
              const { data: rec } = await supabase.auth.refreshSession({ refresh_token: rt });
              session = rec?.session ?? null;
              if (session) clearSessionRecoveryToken();
            }
          }
          if (session?.user?.email) userEmailRef.current = session.user.email;

          // Second verify pass backfills invoice + server email if the first grant skipped finalize.
          await verifyPackSubscription(subscriptionIdFromUrl);

          let subTxn = (await supabase
            .from('credit_transactions')
            .select('order_id, credits_amount, amount')
            .eq('status', 'completed')
            .eq('metadata->>cashfree_subscription_id', subscriptionIdFromUrl)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()).data;

          if (!subTxn?.order_id) {
            subTxn = (await supabase
              .from('credit_transactions')
              .select('order_id, credits_amount, amount')
              .eq('status', 'completed')
              .like('order_id', `subpay_${subscriptionIdFromUrl}%`)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle()).data;
          }

          if (subTxn?.order_id) {
            setOrderId(subTxn.order_id);
            void notifyPaymentSuccess(
              subTxn.order_id,
              subTxn.credits_amount ?? result.credits_per_period ?? 0,
              subTxn.amount,
            );
          }

          // Meta Pixel + Google Ads: subscription purchase conversion
          if (subTxn?.amount) {
            trackPurchase(subTxn.amount, 'INR', subTxn.order_id ?? subscriptionIdFromUrl ?? undefined);
          }
          trackGoogleAdsConversion({
            value: subTxn?.amount != null ? transactionAmountToRupees(subTxn.amount) : undefined,
            currency: 'INR',
            transactionId: subTxn?.order_id ?? subscriptionIdFromUrl ?? undefined,
          });
          trackGa4Subscribe({
            value: subTxn?.amount != null ? transactionAmountToRupees(subTxn.amount) : undefined,
            currency: 'INR',
            transactionId: subTxn?.order_id ?? subscriptionIdFromUrl ?? undefined,
            planId: result.pack_id ?? readCheckoutActivePlanId() ?? 'subscription',
          });

          clearPaymentFlowFlags();
          return;
        }

        if (result.auth_failed || SUBSCRIPTION_TERMINAL_FAIL_STATUSES.has(subStatus)) {
          failSubscription(authFailedMessage);
          return;
        }

        if (attemptNum < SUBSCRIPTION_MAX_ATTEMPTS) {
          setTimeout(() => verifySubscription(attemptNum + 1), SUBSCRIPTION_POLL_MS);
          return;
        }

        if (SUBSCRIPTION_PENDING_STATUSES.has(subStatus)) {
          setStatus('pending');
          setMessage(t('views.paymentSuccess.subscriptionPending', {
            defaultValue: 'Bank authorization is in progress. We will activate your account and email you within 2 minutes.',
          }));
          clearPaymentFlowFlags();
          return;
        }

        failSubscription(authFailedMessage);
      };

      verifySubscription();
      return () => { cancelled = true; };
    }

    // Get order_id from URL parameters
    setOrderId(orderIdFromUrl);
    rememberRetryPlan(readCheckoutActivePlanId());

    if (orderIdFromUrl && urlIndicatesFailure) {
      showPaymentFailure(t('views.paymentSuccess.paymentCancelled'));
      return;
    }

    if (orderIdFromUrl) {
      markPaymentReturnActive(orderIdFromUrl);
      // Rehydrate from storage; if wiped by mobile UPI redirect, recover from cookie
      (async () => {
        let { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          const rt = readSessionRecoveryToken();
          if (rt) {
            const { data: rec } = await supabase.auth.refreshSession({ refresh_token: rt });
            session = rec?.session ?? null;
            if (session) {
              console.log('✅ Session recovered from cookie (one-time payment return)');
              clearSessionRecoveryToken();
            }
          }
        }
        if (redirectingToFailedRef.current) return;
        if (session?.user?.email) userEmailRef.current = session.user.email;
        if (session?.user) {
          const name = session.user.user_metadata?.full_name ||
            session.user.user_metadata?.name ||
            session.user.email?.split('@')[0] || '';
          setCustomerName(name);
        }
      })();
    } else if (isPaymentFlowInProgress()) {
      try {
        sessionStorage.setItem('tab_session_active', Date.now().toString());
      } catch {
        /* best-effort */
      }
    }

    if (!orderIdFromUrl) {
      showPaymentFailure(t('views.paymentSuccess.noOrderId'), { notify: false });
      return;
    }

    verificationStartTimeRef.current = Date.now();

    const maxAttempts = 10;
    const verifyPayment = async (attemptNum = 1, maxAttemptsNum = 10) => {
      if (redirectingToFailedRef.current) return;
      try {
        setAttempt(attemptNum);
        setVerifyingStep('checking');
        console.log(`🔍 [Attempt ${attemptNum}/${maxAttemptsNum}] Verifying payment for order: ${orderIdFromUrl}`);
        
        // First, check database for transaction status
        const { data: transaction, error: txnError } = await supabase
          .from('credit_transactions')
          .select('status, credits_amount, amount, metadata, user_id')
          .eq('order_id', orderIdFromUrl)
          .single();

        if (txnError) {
          console.error('❌ Transaction lookup error:', txnError);
          
          if (attemptNum < maxAttemptsNum) {
            setVerifyingStep('retrying');
            setMessage(t('views.paymentSuccess.verifyingAttempt', { current: attemptNum, max: maxAttemptsNum }));
            setTimeout(() => verifyPayment(attemptNum + 1, maxAttemptsNum), 3000);
            return;
          }
          
          verificationEndTimeRef.current = Date.now();
          showPaymentFailure(t('views.paymentSuccess.couldNotFindRecord', { orderId: orderIdFromUrl }));
          return;
        }

        console.log(`📊 Transaction status: ${transaction.status}`, transaction);
        
        // If still pending, try to verify with Cashfree API directly immediately (don't wait for 3 attempts)
        if (transaction.status === 'pending' || transaction.status === 'processing') {
          console.log(`⚠️ Status still ${transaction.status} at attempt ${attempt}, calling verify-cashfree-payment API...`);
          
          try {
            // Ensure we have a session so the Edge Function receives Authorization (avoids 401).
            // Cascade: getSession → refreshSession → cookie-based recovery (mobile UPI redirect survival).
            let { data: { session: sess } } = await supabase.auth.getSession();
            if (!sess && !sessionRefreshAttemptedRef.current) {
              sessionRefreshAttemptedRef.current = true;
              // Try standard refresh (works if localStorage survived)
              const refreshRes = await supabase.auth.refreshSession();
              sess = refreshRes.data?.session ?? null;
              // If localStorage was wiped (mobile UPI app switch), try cookie-stashed refresh_token
              if (!sess) {
                const recoveryToken = readSessionRecoveryToken();
                if (recoveryToken) {
                  console.log('🔑 Attempting session recovery from cookie-stashed refresh_token');
                  const { data: recovered, error: recErr } = await supabase.auth.refreshSession({ refresh_token: recoveryToken });
                  if (recovered?.session) {
                    sess = recovered.session;
                    console.log('✅ Session recovered from cookie token');
                    clearSessionRecoveryToken();
                  } else {
                    console.warn('❌ Cookie-based session recovery failed:', recErr?.message);
                  }
                }
              }
              if (!sess) {
                const res = await supabase.auth.getSession();
                sess = res.data.session;
              }
            }
            if (sess?.user?.email) userEmailRef.current = sess.user.email;
            if (sess) clearSessionRecoveryToken();
            if (!sess) {
              console.warn('No session available for manual verification – show inline message');
              if (attemptNum >= maxAttemptsNum) {
                verificationEndTimeRef.current = Date.now();
                showPaymentFailure(
                  t('views.paymentSuccess.sessionExpiredVerifyLater') || 'We couldn’t verify your payment right now. Your payment may still have gone through. Go to Dashboard to check your credits.',
                  { planId: transaction.metadata?.plan_id },
                );
              }
              return;
            }

            console.log('📤 Calling manual verification with order_id:', orderIdFromUrl);
            
            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-cashfree-payment`;
            const res = await fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sess.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              },
              body: JSON.stringify({ order_id: orderIdFromUrl }),
            });
            const verifyData = await res.json().catch(() => ({}));
            const verifyError = !res.ok ? { message: (verifyData as { error?: string })?.error ?? 'Request failed', status: res.status } : null;

            console.log('🔍 Manual verification response:', verifyData, 'status:', res.status);
            
            // Gateway says payment failed/cancelled/dropped — stop verifying
            if (res.ok && (
              verifyData?.success === false && verifyData?.status !== 'pending'
              || verifyData?.status === 'failed'
              || verifyData?.status === 'abandoned'
            )) {
              const failureMsg = verifyData?.status === 'abandoned'
                ? t('views.paymentSuccess.paymentCancelled')
                : (verifyData?.message || transaction.metadata?.failure_reason || t('views.paymentSuccess.paymentFailedReason', { reason: 'Payment could not be completed.' }));
              verificationEndTimeRef.current = Date.now();
              if (verifyData?.status !== 'abandoned') {
                firePaymentEmail('payment_failed', { order_id: orderIdFromUrl });
              }
              showPaymentFailure(failureMsg, { planId: transaction.metadata?.plan_id });
              return;
            }
            
            if (verifyError) {
              console.error('❌ Manual verification error details:', {
                message: verifyError.message,
                status: verifyError.status,
              });
              const is401 = verifyError.status === 401 || verifyError.message?.toLowerCase().includes('unauthorized');
              if (is401 && attemptNum >= maxAttemptsNum) {
                verificationEndTimeRef.current = Date.now();
                showPaymentFailure(
                  t('views.paymentSuccess.sessionExpiredVerifyLater') || 'We couldn’t verify your payment right now. Your payment may still have gone through. Go to Dashboard to check your credits.',
                  { planId: transaction.metadata?.plan_id },
                );
                return;
              }
            }
            
            if (res.ok && (verifyData?.success || verifyData?.status === 'completed')) {
              // Payment was actually successful, webhook just didn't fire
              console.log('✅ Manual verification successful!');
              verificationEndTimeRef.current = Date.now();
              clearPaymentFlowFlags();
              setStatus('success');
              const creditsAdded = verifyData?.credits_added || transaction.credits_amount || 10;
              setMessage(t('views.paymentSuccess.paymentVerified', { count: creditsAdded }));
              
              // Store flag to show credits notification AND skip onboarding
              localStorage.setItem('paymentSuccess', JSON.stringify({
                credits: creditsAdded,
                amount: transactionAmountToRupees(transaction.amount),
                planId: transaction.metadata?.plan_id,
                timestamp: new Date().toISOString(),
                skipOnboarding: true
              }));
              
              void notifyPaymentSuccess(orderIdFromUrl, creditsAdded, transaction.amount);

              // Meta Pixel: Purchase (manual verification path)
              if (transaction.amount) {
                trackPurchase(transaction.amount, 'INR', orderIdFromUrl ?? undefined);
              }
              // Google Ads: purchase conversion (manual verification path)
              trackGoogleAdsConversion({
                value: transactionAmountToRupees(transaction.amount),
                currency: 'INR',
                transactionId: orderIdFromUrl ?? undefined,
              });
              trackGa4Purchase({
                value: transactionAmountToRupees(transaction.amount),
                currency: 'INR',
                transactionId: orderIdFromUrl ?? undefined,
                itemName: typeof transaction.metadata?.plan_id === 'string'
                  ? transaction.metadata.plan_id
                  : 'credit_pack',
              });

              // Don't auto-redirect - let user click button to see success message
              return;
            }
          } catch (verifyErr) {
            console.error('❌ Manual verification failed:', verifyErr);
          }
        }

        // Handle different statuses
        switch (transaction.status) {
          case 'completed':
            verificationEndTimeRef.current = Date.now();
            clearPaymentFlowFlags();
            setStatus('success');
            setMessage(t('views.paymentSuccess.paymentSuccessfulCredits', { count: transaction.credits_amount }));
            
            // Store flag to show credits notification AND skip onboarding
            localStorage.setItem('paymentSuccess', JSON.stringify({
              credits: transaction.credits_amount,
              amount: transactionAmountToRupees(transaction.amount),
              planId: transaction.metadata?.plan_id,
              timestamp: new Date().toISOString(),
              skipOnboarding: true
            }));

            void notifyPaymentSuccess(orderIdFromUrl, transaction.credits_amount, transaction.amount);
            
            // Meta Pixel: Purchase (webhook-confirmed path)
            if (transaction.amount) {
              trackPurchase(transaction.amount, 'INR', orderIdFromUrl ?? undefined);
            }
            // Google Ads: purchase conversion (webhook-confirmed path)
            trackGoogleAdsConversion({
              value: transactionAmountToRupees(transaction.amount),
              currency: 'INR',
              transactionId: orderIdFromUrl ?? undefined,
            });
            trackGa4Purchase({
              value: transactionAmountToRupees(transaction.amount),
              currency: 'INR',
              transactionId: orderIdFromUrl ?? undefined,
              itemName: typeof transaction.metadata?.plan_id === 'string'
                ? transaction.metadata.plan_id
                : 'credit_pack',
            });

            // Don't auto-redirect - let user see success and click button
            break;

          case 'failed':
            verificationEndTimeRef.current = Date.now();
            // 📧 Send payment failed email
            firePaymentEmail('payment_failed', { order_id: orderIdFromUrl });
            showPaymentFailure(
              t('views.paymentSuccess.paymentFailedReason', { reason: transaction.metadata?.failure_reason || 'Please try again.' }),
              { planId: transaction.metadata?.plan_id },
            );
            return;

          case 'abandoned':
            verificationEndTimeRef.current = Date.now();
            // 📧 Send payment failed email (abandoned = user cancelled)
            firePaymentEmail('payment_failed', { order_id: orderIdFromUrl });
            showPaymentFailure(t('views.paymentSuccess.paymentCancelled'), { planId: transaction.metadata?.plan_id });
            return;

          case 'pending':
          case 'processing':
            if (attemptNum < maxAttemptsNum) {
              setVerifyingStep('retrying');
              setMessage(t('views.paymentSuccess.processingWait', { current: attemptNum, max: maxAttemptsNum }));
              setTimeout(() => verifyPayment(attemptNum + 1, maxAttemptsNum), 3000);
            } else {
              verificationEndTimeRef.current = Date.now();
              setStatus('pending');
              setMessage(t('views.paymentSuccess.bankProcessingPending', {
                defaultValue: 'Your bank is still processing the payment. We will activate your credits and email you a confirmation within 2 minutes.',
              }));
              clearPaymentFlowFlags();
              return;
            }
            break;

          default:
            verificationEndTimeRef.current = Date.now();
            showPaymentFailure(t('views.paymentSuccess.unknownStatus', { orderId: orderId || '' }), { planId: transaction.metadata?.plan_id });
            return;
        }
      } catch (err) {
        console.error('Payment verification error:', err);

        if (attemptNum < maxAttemptsNum) {
          setTimeout(() => verifyPayment(attemptNum + 1, maxAttemptsNum), 3000);
        } else {
          verificationEndTimeRef.current = Date.now();
          showPaymentFailure(t('views.paymentSuccess.verificationFailed'));
        }
      }
    };

    // Single refresh only when session is missing (inside verifyPayment). Avoid upfront refresh to prevent 429 with other callers.
    // Wait before first verification to allow webhook to process
    const t1 = setTimeout(() => verifyPayment(1, 10), 2000);
    return () => clearTimeout(t1);
  }, [onNavigate, t]);

  // Elapsed time ticker and max-time cap (prevent overshooting)
  useEffect(() => {
    if (status !== 'verifying' || verificationStartTimeRef.current == null) return;
    const interval = setInterval(() => {
      if (redirectingToFailedRef.current) return;
      const start = verificationStartTimeRef.current;
      if (start == null) return;
      const elapsed = Math.floor((Date.now() - start) / 1000);
      setElapsedSeconds(elapsed);
      if (isRecurringFlowRef.current) {
        if (elapsed >= SUBSCRIPTION_MAX_VERIFY_SEC && status === 'verifying') {
          setStatus('pending');
          setMessage(t('views.paymentSuccess.subscriptionPending', {
            defaultValue: 'Bank authorization is in progress. We will activate your account and email you within 2 minutes.',
          }));
          clearPaymentFlowFlags();
        }
        return;
      }
      if (elapsed >= MAX_VERIFICATION_TIME_SEC && status === 'verifying') {
        verificationEndTimeRef.current = Date.now();
        setStatus('pending');
        setMessage(t('views.paymentSuccess.bankProcessingPending', {
          defaultValue: 'Your bank is still processing the payment. We will activate your credits and email you a confirmation within 2 minutes.',
        }));
        clearPaymentFlowFlags();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [status, t]);

  const refElapsed =
    verificationEndTimeRef.current != null && verificationStartTimeRef.current != null
      ? Math.floor((verificationEndTimeRef.current - verificationStartTimeRef.current) / 1000)
      : 0;
  const displayElapsedSeconds = Math.max(0, elapsedSeconds, refElapsed);

  const coverHeadline =
    status === 'success'
      ? t('views.paymentSuccess.coverHeadlineSuccess', { defaultValue: 'You\'re all set' })
      : status === 'pending'
        ? t('views.paymentSuccess.coverHeadlinePending', { defaultValue: 'Almost there' })
        : t('views.paymentSuccess.coverHeadlineVerifying', { defaultValue: 'Confirming your payment' });

  const coverSubtext =
    status === 'success'
      ? t('views.paymentSuccess.coverSubtextSuccess', { defaultValue: 'Your credits are ready — head to the dashboard and keep optimizing.' })
      : status === 'pending'
        ? t('views.paymentSuccess.coverSubtextPending', { defaultValue: 'Bank authorization in progress. You can close this page — we will email you once confirmed.' })
        : t('views.paymentSuccess.coverSubtextVerifying', { defaultValue: 'Secure payment via Cashfree. This usually takes a few seconds.' });

  const handleBackToCheckout = () => {
    navigateFromPaymentResult('checkout', creditCheckoutUrl(retryPlanId));
  };

  return (
    <>
    <SEO
      title="Payment Successful — GoGrabJob"
      description="Your GoGrabJob payment was successful. Credits are ready — continue optimizing resumes from your dashboard."
      canonical="https://www.gograbjob.com/payment-success"
      noIndex
    />
    <PaymentPinSplitLayout
      coverVariant="success"
      coverTitle={coverHeadline}
      coverSubtitle={coverSubtext}
      onBack={handleBackToCheckout}
    >
      <div className="payment-success-status">
        {status === 'verifying' && (
          <div className="payment-success-status-header payment-success-status-header--verifying">
            <span className="payment-success-badge payment-success-badge--verifying">
              <LoadingSpinner size="xs" showText={false} />
              <span>{t('views.paymentSuccess.verifyingPayment')}</span>
            </span>
          </div>
        )}

        <div className="payment-success-body">
            {status === 'verifying' && (
              <div className="py-3 sm:py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <div
                  className="loading-spinner-ring payment-success-spinner w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full border-[3px] border-solid"
                  role="status"
                  aria-label={t('views.paymentSuccess.verifyingPayment')}
                />
                <div className="text-center sm:text-left min-w-0">
                  <h2 className="payment-success-title">
                    {t('views.paymentSuccess.verifyingPayment')}
                  </h2>
                  <p className="payment-success-muted text-sm font-medium mb-0.5">
                    {t(`views.paymentSuccess.${VERIFYING_STEP_KEYS[verifyingStep]}`)}
                  </p>
                  <p className="payment-success-muted text-sm font-medium mb-1">
                    {t('views.paymentSuccess.elapsedTime')}: <span className="font-mono tabular-nums">{formatElapsed(displayElapsedSeconds)}</span>
                  </p>
                  <p className="payment-success-muted text-sm max-w-[260px] mx-auto sm:mx-0 leading-relaxed">
                    {t('views.paymentSuccess.verifyingReassurance')}
                  </p>
                </div>
              </div>
            )}

            {status === 'pending' && (
              <>
                <div className="payment-success-hero-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                  <svg className="w-8 h-8 sm:w-9 sm:h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <h2 className="payment-success-title text-xl sm:text-2xl mb-1.5">
                  {t('views.paymentSuccess.pendingTitle', { defaultValue: 'Bank Authorization in Progress' })}
                </h2>
                <p className="payment-success-muted mb-2 text-sm sm:text-base font-medium">
                  {message}
                </p>
                <div className="payment-success-receipt payment-success-receipt--glass" style={{ borderLeft: '3px solid #f59e0b' }}>
                  <div className="space-y-2 text-sm" style={{ color: 'var(--ds-text-body, #334155)' }}>
                    <p>✅ No money has been deducted yet.</p>
                    <p>📧 You'll receive a confirmation email once the bank completes authorization.</p>
                    <p>⏱ This typically takes 30 seconds to 2 minutes.</p>
                    <p>💡 You can safely close this page — your credits will be activated automatically.</p>
                  </div>
                </div>

                <div className="payment-success-actions-stack mt-4">
                  <button
                    type="button"
                    onClick={() => navigateFromPaymentResult('dashboard', '/dashboard')}
                    className="payment-success-cta payment-success-cta--primary"
                  >
                    {t('views.paymentSuccess.continueToDashboard')}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToCheckout}
                    className="payment-success-cta payment-success-cta-secondary"
                  >
                    {t('views.paymentFailed.retryPayment', { defaultValue: 'Retry Payment' })}
                  </button>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="payment-success-hero-icon payment-success-hero-icon--success">
                  <CheckCircle2 className="w-8 h-8 sm:w-9 sm:h-9" strokeWidth={2.5} aria-hidden />
                </div>
                <h2 className="payment-success-title text-xl sm:text-2xl mb-1.5">
                  {customerName
                    ? t('views.paymentSuccess.thankYouNamed', { name: customerName.split(' ')[0], defaultValue: 'Thank you, {{name}}!' })
                    : t('views.paymentSuccess.thankYou', { defaultValue: 'Thank you!' })}
                </h2>
                <p className="payment-success-muted mb-1 text-sm sm:text-base font-medium">
                  {isRecurringFlow
                    ? t('views.paymentSuccess.subscriptionActivated', { defaultValue: 'Subscription Activated! Auto-applier is ready.' })
                    : t('views.paymentSuccess.paymentSuccessful')}
                </p>
                <p className="payment-success-muted mb-4 sm:mb-6 text-sm sm:text-base">{message}</p>

                <div className="payment-success-receipt payment-success-receipt--glass">
                  <h3 className="payment-success-receipt-heading">{t('views.paymentSuccess.paymentReceipt')}</h3>
                  <div className="payment-success-receipt-rows">
                    {customerName && (
                      <div className="payment-success-receipt-row">
                        <span className="payment-success-receipt-label">{t('views.paymentSuccess.customer', 'Customer')}</span>
                        <span className="payment-success-receipt-value payment-success-receipt-value--truncate">{customerName}</span>
                      </div>
                    )}
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.orderId')}</span>
                      <span className="payment-success-receipt-value payment-success-receipt-value--mono" title={orderId || ''}>
                        {orderId?.substring(0, 6)}…{orderId?.slice(-4)}
                      </span>
                    </div>
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.creditsPurchased')}</span>
                      <span className="payment-success-receipt-value payment-success-receipt-value--accent">
                        {JSON.parse(localStorage.getItem('paymentSuccess') || '{}').credits ?? 10} {t('views.paymentSuccess.credits')}
                      </span>
                    </div>
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.amountPaid')}</span>
                      <span className="payment-success-receipt-value">
                        {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(
                          Number(formatTxnAmountInr(JSON.parse(localStorage.getItem('paymentSuccess') || '{}').amount)) || 0
                        )}
                      </span>
                    </div>
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.status')}</span>
                      <span className="payment-success-receipt-value">
                        <span className="payment-success-receipt-pill payment-success-receipt-pill--success">
                          {t('views.paymentSuccess.completed')}
                        </span>
                      </span>
                    </div>
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.date')}</span>
                      <span className="payment-success-receipt-value">{new Date().toLocaleDateString()}</span>
                    </div>
                    <div className="payment-success-receipt-row">
                      <span className="payment-success-receipt-label">{t('views.paymentSuccess.completedIn')}</span>
                      <span className="payment-success-receipt-value payment-success-receipt-value--mono">{formatElapsed(displayElapsedSeconds)}</span>
                    </div>
                  </div>
                </div>

                <div className="payment-success-actions-stack">
                  <button
                    onClick={handleDownloadReceipt}
                    disabled={downloadingPDF}
                    className="payment-success-cta payment-success-cta-secondary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {downloadingPDF
                      ? t('views.paymentSuccess.generatingPDF', 'Generating PDF...')
                      : t('views.paymentSuccess.downloadInvoicePDF', 'Download Invoice PDF')
                    }
                  </button>

                  <button
                    type="button"
                    onClick={() => navigateFromPaymentResult('dashboard', '/dashboard')}
                    className="payment-success-cta payment-success-cta--primary"
                  >
                    {t('views.paymentSuccess.continueToDashboard')}
                  </button>
                </div>
              </>
            )}
        </div>
      </div>
    </PaymentPinSplitLayout>
    </>
  );
};

export default PaymentSuccessPage;